/**
 * PageShell — desktop page-title row.
 *
 * Under desktop-web `AppTopNav` every tab screen needs an h1 with matching
 * typography, alignment and rhythm so the app feels cohesive. PageShell
 * renders that title row (title, subtitle, right-aligned actions, optional
 * back button) and nothing else — the frame (max-width, outer padding)
 * belongs to the surrounding `ScreenContainer variant='…'`.
 *
 * On mobile/tablet (<1024) it's a passthrough so native stays identical.
 */
import React from 'react'
import { View, Text } from 'react-native'
import { useRouter } from 'expo-router'
import { BackButton } from '../ui/BackButton'
import { useBreakpoint } from '../../lib/hooks/use-breakpoint'
import { useSemanticColors, TEXT_STYLES } from '../../lib/theme/tokens'
import { useTheme } from '../../lib/hooks/use-theme'
import { FONTS, SPACING } from '../../lib/brand'

export interface PageShellProps {
  children: React.ReactNode
  /** h1 page title. If omitted, no title row is rendered. */
  title?: string
  /** Muted subtitle under the title. */
  subtitle?: string
  /** Right-aligned action slot (buttons, filters, etc). */
  actions?: React.ReactNode
  /**
   * Render a back button to the left of the title on desktop. Tabs
   * (Trips, Messages, Explore…) leave this off; nested sub-screens
   * (Notifications, Settings…) turn it on so users can always step back.
   */
  back?: boolean
  /** Custom handler for the back button. Defaults to `router.back()`. */
  onBack?: () => void
}

export function PageShell({ children, title, subtitle, actions, back, onBack }: PageShellProps) {
  const { isDesktop } = useBreakpoint()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const router = useRouter()

  // Mobile + tablet: passthrough — every tab keeps its original mobile header.
  if (!isDesktop) return <>{children}</>

  const hasTitleRow = title || actions || back
  const handleBack = onBack ?? (() => router.back())

  return (
    <View style={{ flex: 1, width: '100%' }}>
      {hasTitleRow ? (
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: SPACING.md,
            marginBottom: SPACING.lg,
          }}
        >
          {back && (
            <BackButton onPress={handleBack} testID="pageshell.back" style={{ marginBottom: 6 }} />
          )}
          <View style={{ flex: 1, minWidth: 0 }}>
            {title ? (
              <Text
                style={{
                  ...TEXT_STYLES.displayBody,
                  color: colors.textPrimary,
                }}
                numberOfLines={2}
                adjustsFontSizeToFit
                minimumFontScale={0.86}
              >
                {title}
              </Text>
            ) : null}
            {subtitle ? (
              <Text
                style={{
                  ...TEXT_STYLES.bodyLg,
                  fontFamily: FONTS.body,
                  color: colors.textSecondary,
                  marginTop: SPACING.xs,
                }}
                numberOfLines={2}
              >
                {subtitle}
              </Text>
            ) : null}
          </View>
          {actions ? (
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: SPACING.sm,
                flexShrink: 0,
              }}
            >
              {actions}
            </View>
          ) : null}
        </View>
      ) : null}

      <View style={{ flex: 1 }}>{children}</View>
    </View>
  )
}
